import { useState } from "react";
import { GoogleLogin } from "@react-oauth/google";
import { useAuth } from "../context/AuthContext.jsx";
import Icon from "./Icon.jsx";

const GOOGLE_CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID || "";

export default function Login() {
  const {
    signInWithGoogleCredential,
    signInWithProvider,
    signInWithOAuth,
    signInAsGuest,
    supabaseConfigured,
  } = useAuth();
  const [error, setError] = useState("");
  const [pending, setPending] = useState("");

  // Real OAuth path: Supabase handles the dance when configured.
  // Demo path: AuthContext fakes a user so the app is still testable locally.
  const handleProvider = async (provider) => {
    setError("");
    setPending(provider);
    if (supabaseConfigured) {
      const res = await signInWithOAuth(provider);
      if (!res.ok) {
        setError(res.error || "Sign-in failed. Try another provider.");
        setPending("");
      }
      return;
    }
    signInWithProvider(provider, {
      id: `demo-${provider}-${Date.now()}`,
      name: `Demo ${provider[0].toUpperCase() + provider.slice(1)} User`,
      email: `demo@${provider}.local`,
    });
    setPending("");
  };

  return (
    <div className="login-shell">
      <div className="login-hero">
        <div>
          <div className="logo" style={{ color: "var(--cream)" }}>
            <span className="logo-mark terra">L</span>
            <span>LearnClaude</span>
          </div>
        </div>
        <div style={{ position: "relative", zIndex: 1 }}>
          <div className="eyebrow" style={{ color: "var(--terracotta-2)", marginBottom: 24 }}>
            A gamified school for AI
          </div>
          <h1
            className="serif"
            style={{
              fontSize: "clamp(44px, 6vw, 72px)",
              lineHeight: 0.98,
              letterSpacing: "-0.02em",
              margin: "0 0 20px",
              color: "var(--cream)",
            }}
          >
            Learn Claude,<br />
            <span style={{ fontStyle: "italic", color: "var(--terracotta-2)" }}>
              one craft at a time.
            </span>
          </h1>
          <p style={{ fontSize: 17, color: "var(--cream-3)", maxWidth: 420, lineHeight: 1.55 }}>
            Sign in to save your streak, earn gems, and pick up where you left off. Your progress stays on
            this device until you do.
          </p>
        </div>
        <div style={{ display: "flex", gap: 18, color: "var(--cream-3)", fontSize: 13, position: "relative", zIndex: 1 }}>
          <div><strong style={{ color: "var(--cream)" }}>42k+</strong> learners</div>
          <div><strong style={{ color: "var(--cream)" }}>4.9★</strong> avg rating</div>
          <div><strong style={{ color: "var(--cream)" }}>96%</strong> finish rate</div>
        </div>
      </div>

      <div className="login-form">
        <div className="login-card">
          <div className="eyebrow" style={{ marginBottom: 10 }}>Get started</div>
          <h2
            className="serif"
            style={{ fontSize: 40, margin: "0 0 8px", letterSpacing: "-0.02em" }}
          >
            Sign in to LearnClaude
          </h2>
          <p style={{ color: "var(--ink-3)", fontSize: 14, marginBottom: 28 }}>
            Pick your preferred way to continue. No passwords, no fuss.
          </p>

          {error && (
            <div
              role="alert"
              style={{
                padding: "10px 14px",
                borderRadius: 10,
                background: "#F4D9D9",
                border: "1px solid var(--heart)",
                color: "#8A2A2A",
                fontSize: 13,
                marginBottom: 16,
              }}
            >
              {error}
            </div>
          )}

          <div style={{ display: "grid", gap: 10 }}>
            {supabaseConfigured ? (
              <button
                className="provider-btn"
                onClick={() => handleProvider("google")}
                disabled={pending !== ""}
                aria-label="Continue with Google"
              >
                <GoogleGlyph /> Continue with Google
              </button>
            ) : GOOGLE_CLIENT_ID ? (
              <div style={{ display: "flex", justifyContent: "center" }}>
                <GoogleLogin
                  onSuccess={(res) => {
                    if (res.credential) signInWithGoogleCredential(res.credential);
                    else setError("Google did not return a credential.");
                  }}
                  onError={() => setError("Google sign-in failed. Try another provider.")}
                  useOneTap={false}
                  theme="outline"
                  size="large"
                  width="360"
                />
              </div>
            ) : (
              <button
                className="provider-btn"
                onClick={() => handleProvider("google")}
                aria-label="Continue with Google (demo)"
              >
                <GoogleGlyph /> Continue with Google
                <span className="chip" style={{ marginLeft: 8, fontSize: 10 }}>demo</span>
              </button>
            )}

            <button
              className="provider-btn dark"
              onClick={() => handleProvider("github")}
              disabled={pending !== ""}
              aria-label="Continue with GitHub"
            >
              <GithubGlyph /> Continue with GitHub
              {!supabaseConfigured && (
                <span className="chip" style={{ marginLeft: 8, fontSize: 10, background: "#2a241d", borderColor: "#3a332b", color: "#cdbfaa" }}>
                  demo
                </span>
              )}
            </button>

            <button
              className="provider-btn"
              onClick={() => handleProvider("apple")}
              disabled={pending !== ""}
              aria-label="Continue with Apple"
            >
              <AppleGlyph /> Continue with Apple
              {!supabaseConfigured && (
                <span className="chip" style={{ marginLeft: 8, fontSize: 10 }}>demo</span>
              )}
            </button>
          </div>

          <div className="divider">or</div>

          <button
            className="btn btn-ghost"
            style={{ width: "100%", justifyContent: "center" }}
            onClick={() => signInAsGuest()}
          >
            Continue as guest <Icon name="arrow-right" size={14} />
          </button>

          <p
            style={{
              marginTop: 24,
              fontSize: 11,
              color: "var(--ink-4)",
              lineHeight: 1.5,
              textAlign: "center",
            }}
          >
            By continuing you agree to our{" "}
            <a href="#" style={{ color: "var(--terracotta)" }}>Terms</a> and{" "}
            <a href="#" style={{ color: "var(--terracotta)" }}>Privacy Policy</a>.
          </p>
        </div>
      </div>
    </div>
  );
}

function GoogleGlyph() {
  return (
    <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
      <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.9 29.3 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3 0 5.8 1.1 7.9 3l5.7-5.7C34 6 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.2-.1-2.3-.4-3.5z"/>
      <path fill="#FF3D00" d="m6.3 14.7 6.6 4.8C14.5 16 18.9 12.8 24 12.8c3 0 5.8 1.1 7.9 3l5.7-5.7C34 6 29.3 4 24 4 16.4 4 9.8 8.3 6.3 14.7z"/>
      <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2c-2 1.5-4.6 2.4-7.2 2.4-5.3 0-9.7-3.1-11.5-7.6l-6.5 5c3.4 6.6 10 11.1 17.5 11.1z"/>
      <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.4-2.4 4.5-4.5 5.9l6.2 5.2C41 35 44 29.9 44 24c0-1.2-.1-2.3-.4-3.5z"/>
    </svg>
  );
}

function GithubGlyph() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M12 .5C5.73.5.5 5.73.5 12c0 5.08 3.29 9.39 7.86 10.91.58.1.79-.25.79-.56v-2c-3.2.69-3.87-1.54-3.87-1.54-.52-1.32-1.28-1.67-1.28-1.67-1.05-.72.08-.7.08-.7 1.16.08 1.77 1.19 1.77 1.19 1.03 1.76 2.7 1.25 3.36.95.1-.74.4-1.25.73-1.54-2.56-.29-5.24-1.28-5.24-5.7 0-1.26.45-2.3 1.19-3.11-.12-.3-.52-1.49.11-3.1 0 0 .97-.31 3.18 1.18a11 11 0 0 1 5.8 0c2.2-1.49 3.17-1.18 3.17-1.18.63 1.61.24 2.8.12 3.1.74.81 1.19 1.85 1.19 3.11 0 4.43-2.69 5.4-5.26 5.69.41.35.78 1.05.78 2.12v3.14c0 .31.21.67.8.56A11.5 11.5 0 0 0 23.5 12C23.5 5.73 18.27.5 12 .5z"/>
    </svg>
  );
}

function AppleGlyph() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M17.564 13.08c-.025-2.5 2.042-3.708 2.137-3.765-1.167-1.706-2.983-1.94-3.627-1.966-1.544-.156-3.014.91-3.8.91-.783 0-1.99-.887-3.273-.862-1.685.024-3.236.98-4.102 2.49-1.751 3.034-.447 7.525 1.259 9.986.83 1.204 1.82 2.556 3.117 2.508 1.248-.05 1.72-.808 3.23-.808 1.51 0 1.935.808 3.26.784 1.345-.024 2.198-1.227 3.023-2.435.952-1.398 1.345-2.753 1.37-2.824-.03-.013-2.625-1.008-2.65-4.018zM15.073 5.7c.695-.84 1.163-2.01 1.035-3.172-1 .04-2.21.665-2.928 1.504-.644.743-1.207 1.93-1.054 3.076 1.114.085 2.25-.565 2.947-1.408z"/>
    </svg>
  );
}
